import React from "react";
import gql from "graphql-tag";
import { Box, Flex, Text } from "@atoms";
import { getPaginatedNodes } from "@utils";
import { addsDirectFundraiserGroup } from "@services";
import { FundsPageEarningsFundraisersList } from "../FundsPageEarningsFundraisersList";
import { FundsPageEarningsGroupsListAnonymousFragment } from "./__generated__/FundsPageEarningsGroupsListAnonymousFragment";

// == Types ================================================================
interface IProps {
  campaign: FundsPageEarningsGroupsListAnonymousFragment;
}

// == Constants ============================================================

FundsPageEarningsGroupsListAnonymous.fragments = gql`
  fragment FundsPageEarningsGroupsListAnonymousFragment on Campaign {
    id
    name
    privacyLevel
    isViewerMember @include(if: $hasCurrentUser)
    isViewerOwner @include(if: $hasCurrentUser)
    viewerFundraiser @include(if: $hasCurrentUser) {
      id
      userId
      name
      group {
        id
        name
      }
    }
    directAmountRaised {
      dollarsAmount
      formatted
    }
    groups {
      id
      name
      amountRaised {
        dollarsAmount
        formatted
      }
      paginatedFundraisers(first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            userId
            ...FundsPageEarningsFundraisersListFundraisersFragment
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    noGroupFundraisers {
      id
      name
      amountRaised {
        dollarsAmount
        formatted
      }
      paginatedFundraisers(first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            userId
            ...FundsPageEarningsFundraisersListFundraisersFragment
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    ...FundsPageEarningsFundraisersListViewerFundraiserFragment
    ...addsDirectFundraiserGroupFragment
  }
  ${FundsPageEarningsFundraisersList.fragments.viewerFundraiser}
  ${FundsPageEarningsFundraisersList.fragments.fundraisers}
  ${addsDirectFundraiserGroup.fragment}
`;

// == Component ============================================================

export default function FundsPageEarningsGroupsListAnonymous({ campaign }: IProps) {
  const { groups, viewerFundraiser, noGroupFundraisers, privacyLevel } = campaign;
  const directFundraiserGroup = addsDirectFundraiserGroup(campaign);
  const combinedGroups = [...groups, noGroupFundraisers, directFundraiserGroup];

  return (
    <Box width={1}>
      {combinedGroups.map((group) => {
        const {
          id: groupId,
          name,
          amountRaised: { formatted },
          paginatedFundraisers: { edges },
        } = group;

        const fundraisers = getPaginatedNodes(edges);

        const isGroupDirect = group?.isDirect;
        const isViewerGroup = viewerFundraiser?.group?.id === groupId;
        const isViewerGroupNoGroup = !viewerFundraiser?.group && groupId === "NO_GROUP";
        const isUserGroup = !isGroupDirect && (isViewerGroup || isViewerGroupNoGroup);

        return (
          <Box bg="lightBg" key={`${groupId} ${name}`}>
            <Flex alignItems="row" justifyContent="space-between" padding={3}>
              <Box>
                <Text fontSize={[1, 1, 2]}>{name}</Text>
              </Box>
              <Box>
                <Text fontSize={[1, 1, 2]}>{formatted || "$0.00"}</Text>
              </Box>
            </Flex>
            {isUserGroup && !!viewerFundraiser && (
              <FundsPageEarningsFundraisersList
                fundraisers={fundraisers}
                privacyLevel={privacyLevel}
                viewerFundraiser={viewerFundraiser}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// == Styles ===============================================================
